import { Component, HostBinding, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CellClickedEvent, RowNode } from 'ag-grid-community';
import * as _ from 'underscore';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, GenericColDef, GridContext } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, ObjectActionSet, Permissions } from '../../../../models/permission';
import { PermissionDetail, Role, RolePermissionTabType, RolePermissionViewData, SecureObjectGroupType, SecureObjectPermissionLevel } from '../../../../models/role';
import { isBoolean, isUndefined } from '../../../../models/utils';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';


@Component({
    standalone: false,
    selector: 'res-system-role-permissions',
    templateUrl: './system-role-permissions.component.html',
    styleUrls: ['./system-role-permissions.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class SystemRolePermissionsComponent implements OnInit {
    readonly SystemRolePermissionTabType = RolePermissionTabType;
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('panelGrid', { static: true }) panelGrid: GenericGridComponent;
    @ViewChild('navigationTreeNodeGrid', { static: true }) navigationTreeNodeGrid: GenericGridComponent;
    @ViewChild('settingGrid', { static: true }) settingGrid: GenericGridComponent;
    @ViewChild('eDiscoveryForm', { static: true }) eDiscoveryForm: NgForm;

    tabType: RolePermissionTabType;

    // member variables
    name: 'System Role Permissions';
    automationId: 'system-settings-tree-general-roles';
    icon: 'fa-user-md';
    iconColor: 'color-yellow-07';
    childPadding: 5;
    handle: '';
    templateName: string;
    isBusy = false;
    isBusyMessage = 'Loading ...';
    validChanges = true;

    helpIndex: HelpIndex;
    gridContext: GridContext;
    hasPermissions = false;

    // variables - across tabs
    selectedRole: Role;
    viewData: RolePermissionViewData;
    roleOriginalObjectActionList: Map<string, ObjectActionSet>;

    // Setup panels

    // panel grid
    panelGridName = 'Panel';
    panelGridColDefs: any;
    panelAutomationId = 'panel-grid-automation-id';

    // setting grid
    settingGridName = 'Setting';
    settingGridColDefs: any;
    settingAutomationId = 'setting-grid-automation-id';

    dirtyCount = 0;

    // Methods
    get isDirty(): boolean {
        if (this.eDiscoveryForm) {
            return this.eDiscoveryForm.dirty;
        }
        return false;
    }

    set isDirty(value: boolean) {
        if (this.eDiscoveryForm && this.eDiscoveryForm.control) {
            if (value) {
                this.eDiscoveryForm.control.markAsDirty();
            } else {
                this.eDiscoveryForm.control.markAsPristine();
            }
        }
    }

    rowClassRules = {
        'roles-header-row': (params: any): any => {
            return (params.data && params.data.isRoleHeader);
        },
    };

    constructor(private commonService: CommonService, private restService: RestService) {
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    ngOnInit() {
        this.helpIndex = HelpIndex.system_roles_permissions_dlg;
    }

    loadData(): void {
        //TODO - require to set the contextHandle to 'super_system_customer' if logged in with system admin with @org name - currently uses the org name as contextHandle
        this.isBusyMessage = 'Loading System Role Permissions ...';
        this.isBusy = true;
        this.isDirty = false;
        if (this.viewData == null) {
            this.restService.post(Endpoint.permissionManager_getSecureObjectGroups, { systemScope: true }).then(responseAllFields => {
                if (responseAllFields?.status === 'SUCCESS' && responseAllFields.groupedSecureObjectTypes) {
                    // initialize all the default role view data related before applying role specific settings
                    this.viewData = new RolePermissionViewData(responseAllFields.groupedSecureObjectTypes);
                    if (this.selectedRole?.handle) {
                        this.applyRole();
                    }
                }
            }, () => {
                this.isBusy = false;
            });
        } else {
            if (this.selectedRole && this.selectedRole.handle) {
                this.applyRole();
            }
        }
    }

    selectedTreeItemChanged(node: GenericTreeNode): void {
        if (node && node.data) {
            this.selectedRole = new Role(node.data);
            this.templateName = node.data.name;
            this.handle = node.data.handle;
        }
        this.hasPermissions = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_ROLE, ActionType.CREATE);
        this.initializeGridColumns();
        this.loadData();
    }

    initializeGridColumns(): void {
        this.panelGridColDefs = this.getStatusColumnDefs();
        this.settingGridColDefs = this.getConfigurationColumnDefs();
    }

    initializeGrids(viewData: RolePermissionViewData) {
        if (viewData && viewData.groupsToPermissionsMap) {
            const groups = Array.from(viewData.groupsToPermissionsMap.keys());
            const provisioningPermissionDetails = [];
            const templatesPermissionDetails = [];
            const configurationPermissionDetails = [];
            _.each(groups, (secureObjectGroupType) => {
                switch (secureObjectGroupType) {
                    case SecureObjectGroupType.SYSTEM_ACTIONS:
                        {
                            if (this.panelGrid) {
                                this.panelGrid.setColumnDefs(this.panelGridColDefs);
                                const systemActionsList = this.viewData.getPermissionDetailsByGroup(secureObjectGroupType);
                                if (systemActionsList?.length > 0) {
                                    this.panelGrid.setData(systemActionsList, true);
                                    this.panelGrid.refreshColumns();
                                }
                            }
                        }
                        break;
                    case SecureObjectGroupType.SYSTEM_PROVISIONING:
                        {
                            this.setObjectPermissionDetails(provisioningPermissionDetails, secureObjectGroupType, 'Provisioning');
                        }
                        break;
                    case SecureObjectGroupType.SYSTEM_CONFIGURATION:
                        {
                            this.setObjectPermissionDetails(configurationPermissionDetails, secureObjectGroupType, 'Configuration');
                        }
                        break;
                    case SecureObjectGroupType.SYSTEM_SETTINGS:
                        {
                            this.setObjectPermissionDetails(templatesPermissionDetails, secureObjectGroupType, ' Templates');
                        }
                        break;
                    default:
                        break;
                }
            });

            const allPermissionDetails = configurationPermissionDetails.concat(provisioningPermissionDetails, templatesPermissionDetails);
            if (this.settingGrid?.gridApi && allPermissionDetails?.length > 0) {
                if (this.settingGrid) {
                    this.settingGrid.setColumnDefs(this.settingGridColDefs);
                    this.settingGrid.setData(allPermissionDetails, true);
                    this.settingGrid.refreshColumns();
                }
            }
        }
        // window.dispatchEvent(new Event('resize'));
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent) => {
        if (rowNode && rowNode.data && action && event) {
            if (action === CellActionType.checkbox) {
                if (this.viewData && rowNode.data.secureObjectType && event.colDef.colId && event.colDef.field) {
                    const currentDetails = this.viewData.rolePermissionDetailViewDataMap.get(rowNode.data.secureObjectType);
                    const value = rowNode.data[event.colDef.field];
                    if (isBoolean(value)) {
                        const prevDirty = this.viewData.isDirty(rowNode.data.secureObjectType, SecureObjectPermissionLevel.SYSTEM);
                        const updatedPermissionDetails = this.viewData.updatePermissionDetail(rowNode.data.secureObjectType, currentDetails, event.colDef.colId, SecureObjectPermissionLevel.SYSTEM, !value);
                        const nowDirty = this.viewData.isDirty(rowNode.data.secureObjectType, SecureObjectPermissionLevel.SYSTEM);
                        rowNode.setData(updatedPermissionDetails);
                        if (prevDirty && !nowDirty) {
                            this.dirtyCount--;
                        } else if (!prevDirty && nowDirty) {
                            this.dirtyCount++;
                        }
                        this.isDirty = this.dirtyCount > 0;
                    }
                }
            }
        }
    }

    private setObjectPermissionDetails(objectPermissionDetails: any[], secureObjectGroupType: any, headerName: string) {
        const header = new PermissionDetail();
        header.displayValue = headerName;
        header.isRoleHeader = true;
        objectPermissionDetails.push(header);
        _.each(this.viewData.getPermissionDetailsByGroup(secureObjectGroupType), (object) => {
            objectPermissionDetails.push(object);
        });
    }

    isFieldDisabled(): boolean {
        return this.selectedRole?.isAdministratorRole(Role.systemAdministrator) || this.selectedRole?.isAdministratorRole(Role.systemITAdministrator) || !this.hasPermissions;
    }

    setOriginalActionList(objectActionList: ObjectActionSet[]): void {
        this.roleOriginalObjectActionList = new Map<string, ObjectActionSet>();
        _.each(objectActionList, (anObjectActionList) => {
            const data = new ObjectActionSet(anObjectActionList);
            this.roleOriginalObjectActionList.set(data.type, data);
        });
    }

    saveChanges() {
        this.isBusyMessage = 'Saving Role Permissions ...';
        this.isBusy = true;
        const finalRolePermissions = this.viewData.getObjectActionList(this.roleOriginalObjectActionList);

        if (!isUndefined(finalRolePermissions) && (finalRolePermissions !== null)) {
            this.isDirty = false;
            this.restService.post(Endpoint.orgManager_updateRole, {
                objectActionList: finalRolePermissions,
                name: this.templateName,
                handle: this.handle,
                systemScope: true
            }).then(response => {
                if (response && (response.status === 'SUCCESS')) {
                    this.selectedRole.objectActionList = response.role.objectActionList;
                    this.viewData.resetState();
                    this.setOriginalActionList(response.role.objectActionList);
                }
                this.isBusy = false;
                this.isDirty = false;
            }, () => {
                this.isBusy = false;
                this.isDirty = true;
            });
        }
    }

    discardChanges() {
        // clear all viewData related structures and load again.
        this.viewData = null;
        this.dirtyCount = 0;
        this.loadData();
    }

    createRoleError(roleName: string): void {
        this.commonService.openErrorModal('Create role "' + roleName + '" failed.');
    }

    getRole(roleHandle: string): Role {
        let retrievedRole = null;
        if (this.handle) {
            this.restService.post(Endpoint.permissionManager_getCombinedUserRole, { entityHandle: roleHandle, systemScope: true }).then(roleResponse => {
                if (roleResponse && roleResponse.status === 'SUCCESS') {
                    retrievedRole = roleResponse.getRole();
                }
            }, () => {
                this.isBusy = false;
            });
        }

        let finalRole = null;
        if (retrievedRole !== null) {
            finalRole = new Role(retrievedRole);
        }
        return finalRole;
    }

    applyRole(): void {
        this.restService.post(Endpoint.orgManager_getRole, { handle: this.selectedRole.handle, systemScope: true }).then(roleResponse => {
            if (roleResponse?.status === 'SUCCESS') {
                const currentRole = roleResponse.role;
                if (currentRole?.objectActionList) {
                    this.selectedRole.objectActionList = currentRole.objectActionList;
                    this.setOriginalActionList(this.selectedRole.objectActionList);

                    if (this.roleOriginalObjectActionList?.size > 0 && this.viewData) {
                        // update permission details for the selected role.
                        const objectActionListValues = Array.from(this.roleOriginalObjectActionList.values());
                        this.viewData.applyRoleToPermissionDetails(objectActionListValues, this.selectedRole);
                        this.initializeGrids(this.viewData);
                    }
                } else {
                    console.log('Error: unable to get role with handle <' + this.selectedRole.handle + '>');
                }
            }
            this.isBusy = false;
            this.isBusyMessage = '';
        }, () => {
            this.isBusyMessage = '';
            this.isBusy = false;
        });
    }

    getStatusColumnDefs(): Array<GenericColDef> {
        const panelGridColDefs = new Array<GenericColDef>(
            {
                headerName: 'Status Tree Node',
                field: 'displayValue',
                colId: 'displayValue',
                automationId: this.panelAutomationId,
                cellTemplateType: CellTemplateType.nameMultipleIconsLabel,
                minWidth: 100,
                lockVisible: true
            },
            {
                headerName: 'View',
                field: 'systemViewState',
                colId: 'VIEW',
                automationId: this.panelAutomationId,
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true,
            },
            {
                headerName: 'Add/Edit',
                field: 'systemCreateState',
                colId: 'CREATE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                automationId: this.panelAutomationId,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Delete',
                field: 'systemDeleteState',
                colId: 'DELETE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                automationId: this.panelAutomationId,
                lockVisible: true
            }
        );
        return panelGridColDefs;
    }

    getConfigurationColumnDefs(): Array<any> {
        const settingGridColDefs = new Array<any>(
            {
                headerName: 'Settings Tree Node',
                field: 'displayValue',
                colId: 'displayValue',
                automationId: this.panelAutomationId,
                cellTemplateType: CellTemplateType.nameMultipleIconsLabel,
                width: 463,
                minWidth: 100,
                lockVisible: true
            },
            {
                headerName: 'View',
                field: 'systemViewState',
                colId: 'VIEW',
                automationId: this.panelAutomationId,
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true,
            },
            {
                headerName: 'Add/Edit',
                field: 'systemCreateState',
                colId: 'CREATE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                automationId: this.panelAutomationId,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Delete',
                field: 'systemDeleteState',
                colId: 'DELETE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                automationId: this.panelAutomationId,
                lockVisible: true
            }
        );
        return settingGridColDefs;
    }
}
